import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { COVERAGE_STATES, COVERAGE_STATE_LABEL, type CoverageState } from "@ti/core";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { ErrorPanel } from "@/components/layout/page";
import { Card } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/input";
import { FilterPill } from "@/features/coverage/filter-pill";
import { countByState } from "@/features/coverage/policy-bits";
import { ScenarioSheet, ScenarioTable, sortScenarios } from "@/features/coverage/scenario-table";
import { q } from "@/lib/api";

interface ScenarioSearch {
  state?: CoverageState;
  agentId?: string;
  policyId?: string;
}

const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);

export const Route = createFileRoute("/_app/coverage/scenarios")({
  validateSearch: (s: Record<string, unknown>): ScenarioSearch => ({
    state: typeof s.state === "string" && (COVERAGE_STATES as readonly string[]).includes(s.state) ? (s.state as CoverageState) : undefined,
    agentId: str(s.agentId),
    policyId: str(s.policyId),
  }),
  component: ScenariosPage,
});

function ScenariosPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const scenarios = useQuery(q.scenarios());
  const endorsements = useQuery(q.endorsements());
  const agents = useQuery(q.agents());
  const policies = useQuery(q.policies());
  const [text, setText] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const all = useMemo(() => sortScenarios(scenarios.data ?? []), [scenarios.data]);
  const scoped = all.filter((s) => (!search.agentId || s.agentId === search.agentId) && (!search.policyId || s.policyId === search.policyId));
  const counts = useMemo(() => countByState(scoped), [scoped]);

  const needle = text.trim().toLowerCase();
  const rows = scoped.filter(
    (s) =>
      (!search.state || s.state === search.state) &&
      (!needle || s.key.toLowerCase().includes(needle) || s.envelope.toLowerCase().includes(needle) || s.actionClass.toLowerCase().includes(needle)),
  );
  const selected = all.find((s) => s.id === selectedId) ?? null;

  const setSearch = (patch: Partial<ScenarioSearch>) => void navigate({ search: (prev: ScenarioSearch) => ({ ...prev, ...patch }), replace: true });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Filter by coverage state" className="flex flex-wrap items-center gap-1">
          <FilterPill active={!search.state} onClick={() => setSearch({ state: undefined })} label="All" count={scoped.length} />
          {COVERAGE_STATES.map((s) => (
            <FilterPill key={s} active={search.state === s} onClick={() => setSearch({ state: search.state === s ? undefined : s })} label={COVERAGE_STATE_LABEL[s]} count={counts[s]} />
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select aria-label="Filter by policy" value={search.policyId ?? ""} onChange={(e) => setSearch({ policyId: e.target.value || undefined })} className="w-56">
            <option value="">All policies</option>
            {policies.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Select aria-label="Filter by agent" value={search.agentId ?? ""} onChange={(e) => setSearch({ agentId: e.target.value || undefined })} className="w-44">
            <option value="">All agents</option>
            {agents.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-3" aria-hidden />
            <Input aria-label="Search scenarios" placeholder="Search key or envelope" value={text} onChange={(e) => setText(e.target.value)} className="w-52 pl-8" />
          </div>
        </div>
      </div>

      {scenarios.error ? (
        <ErrorPanel error={scenarios.error} reset={() => void scenarios.refetch()} />
      ) : (
        <Card className="overflow-hidden">
          <ScenarioTable
            scenarios={scenarios.isPending ? undefined : rows}
            loading={scenarios.isPending}
            endorsements={endorsements.data}
            showPolicy={!search.policyId}
            onSelect={(s) => setSelectedId(s.id)}
            empty={{
              title: "No scenarios match",
              description: search.state || search.agentId || search.policyId || needle ? "Try clearing the state, policy, agent or search filters." : "Scenarios appear once a policy covering your agents is bound.",
            }}
            footer={
              rows.length > 0 ? (
                <div className="border-t px-3 py-2 text-xs text-fg-3 tabular">
                  {rows.length} of {all.length} scenarios
                </div>
              ) : null
            }
          />
        </Card>
      )}

      <ScenarioSheet scenario={selected} endorsements={endorsements.data} onClose={() => setSelectedId(null)} />
    </div>
  );
}
